import { defineStore } from "pinia";
import apiClient from "../services/apiClient";

// A skill call can take two provider round-trips (validation re-ask), so allow
// more than the default 30s before giving up.
const SKILL_TIMEOUT_MS = 65000;
const SKILL_REQUEST_OPTIONS = { showSuccessAlert: false, showErrorAlert: true };

/**
 * AI skills (AI_AGENT_SYSTEM_SPEC.md): report review, trait suggestions and
 * feedback for the report form, plus tenant guidance and usage reports for admins.
 * Skills only return suggestions; nothing is written until the report is saved.
 */
export const useAiStoreRefactor = defineStore("aiRefactor", {
  state: () => ({
    checking: false,
    reviewing: false,
    suggesting: false,
    tenantSettings: [],
    loading: false,
    error: null,
    usageReport: null,
    usageReportLoading: false,
    usageReportError: null,
  }),

  actions: {
    /**
     * Runs several draft skills in one request. Each key of the result is a skill:
     * either its result (with `usage_log_id`) or `{ error: { code, message } }`.
     * @param {{ text: string, skills: string[], reportCategoryId?: number,
     *   selectedTraitIds?: number[], existingMentions?: { type: string, id: number }[] }} draft
     */
    async draftCheck({
      text,
      skills,
      reportCategoryId,
      selectedTraitIds = [],
      existingMentions = [],
    }) {
      this.checking = true;
      try {
        return await apiClient.post(
          "/api/v1/ai/draft_check",
          {
            text,
            skills,
            report_category_id: reportCategoryId || null,
            selected_trait_ids: selectedTraitIds,
            existing_mentions: existingMentions,
          },
          { timeout: SKILL_TIMEOUT_MS },
          SKILL_REQUEST_OPTIONS,
        );
      } finally {
        this.checking = false;
      }
    },

    /** The stored AI briefing of an account, or null when there is none yet. */
    async fetchAccountBriefing(accountId) {
      const data = await apiClient.get(
        `/api/v1/accounts/${accountId}/ai_briefing`,
        {},
        { showErrorAlert: false },
      );
      return data?.briefing || null;
    },

    /** Generates or refreshes the stored briefing; returns the new briefing. */
    async generateAccountBriefing(accountId) {
      const data = await apiClient.post(
        "/api/v1/ai/account_briefing",
        { account_id: accountId },
        { timeout: SKILL_TIMEOUT_MS },
        SKILL_REQUEST_OPTIONS,
      );
      return data.briefing;
    },

    /**
     * @param {{ text: string, reportCategoryId?: number }} draft - text with mention placeholders
     * @returns {Promise<{ usage_log_id: number, feedback_bullets: Array<{ text: string, severity: string }>, suggested_rewrite: string, rewrite_applicable: boolean }>}
     */
    async reportReview({ text, reportCategoryId }) {
      this.reviewing = true;
      try {
        return await apiClient.post(
          "/api/v1/ai/report_review",
          { text, report_category_id: reportCategoryId || null },
          { timeout: SKILL_TIMEOUT_MS },
          SKILL_REQUEST_OPTIONS,
        );
      } finally {
        this.reviewing = false;
      }
    },

    /**
     * @param {{ text: string }} draft - text with mention placeholders
     * @returns {Promise<{ usage_log_id: number, suggestions: { trait_id: number, reason: string }[] }>}
     */
    async traitSuggestions({ text }) {
      this.suggesting = true;
      try {
        return await apiClient.post(
          "/api/v1/ai/trait_suggestions",
          { text },
          { timeout: SKILL_TIMEOUT_MS },
          SKILL_REQUEST_OPTIONS,
        );
      } finally {
        this.suggesting = false;
      }
    },

    /**
     * Records what the worker did with a review (applied/dismissed) and an optional
     * thumbs rating. Best effort: a failure never interrupts the worker.
     */
    async feedback({ usageLogId, outcome, rating = null }) {
      try {
        await apiClient.post(
          "/api/v1/ai/feedback",
          { usage_log_id: usageLogId, outcome, rating },
          {},
          { showSuccessAlert: false, showErrorAlert: false },
        );
        return true;
      } catch (error) {
        console.warn(
          "AI feedback could not be recorded",
          error?.response?.status,
        );
        return false;
      }
    },

    async fetchTenantSettings() {
      return this._load("loading", "error", async () => {
        this.tenantSettings = await apiClient.get(
          "/api/v1/ai_tenant_prompt_settings",
        );
        return this.tenantSettings;
      });
    },

    async saveTenantSetting(key, guidance) {
      const saved = await apiClient.put(
        `/api/v1/ai_tenant_prompt_settings/${key}`,
        { guidance },
        {},
        { successMessage: "Opgeslagen" },
      );
      const index = this.tenantSettings.findIndex(
        (setting) => setting.key === key,
      );
      if (index === -1) this.tenantSettings.push(saved);
      else this.tenantSettings.splice(index, 1, saved);
      return saved;
    },

    /**
     * @param {{ scope?: "tenant"|"global", from?: string, to?: string }} params - dates as YYYY-MM-DD
     */
    async fetchUsageReport({ scope = "tenant", from = null, to = null } = {}) {
      return this._load("usageReportLoading", "usageReportError", async () => {
        const query = new URLSearchParams({ scope });
        if (from) query.set("from", from);
        if (to) query.set("to", to);
        this.usageReport = await apiClient.get(
          `/api/v1/ai_usage_reports?${query}`,
        );
        return this.usageReport;
      });
    },

    /**
     * A page of individual AI requests of this organisation (metrics only, no text).
     * Empty filter values are left out.
     * @param {Object} params - key, variant, version, user_id, outcome, from, to, page, per_page
     * @returns {Promise<{ data: Object[], meta: Object }>}
     */
    async fetchUsageLogs(params = {}) {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([name, value]) => {
        if (value !== null && value !== undefined && value !== "")
          query.set(name, value);
      });
      const suffix = query.toString() ? `?${query}` : "";
      return apiClient.get(
        `/api/v1/ai_usage_logs${suffix}`,
        {},
        { showErrorAlert: false },
      );
    },

    /** One AI request with its instruction, input and answer (client text). */
    async fetchUsageLog(id) {
      return apiClient.get(
        `/api/v1/ai_usage_logs/${id}`,
        {},
        { showErrorAlert: false },
      );
    },

    /**
     * For a request whose data was not stored (the summary of a person): that data
     * rebuilt from the reports as they are now. Client text; nothing is sent to the AI.
     * @returns {Promise<{ user_prompt: string, missing_report_ids: number[] }>}
     */
    async fetchUsageLogInputPreview(id) {
      return apiClient.get(
        `/api/v1/ai_usage_logs/${id}/input_preview`,
        {},
        { showErrorAlert: false },
      );
    },

    // Runs fn while tracking its own loading/error state fields.
    async _load(loadingKey, errorKey, fn) {
      this[loadingKey] = true;
      this[errorKey] = null;
      try {
        return await fn();
      } catch (error) {
        this[errorKey] =
          error?.response?.data?.message ||
          error.message ||
          "Er is een fout opgetreden";
        throw error;
      } finally {
        this[loadingKey] = false;
      }
    },
  },
});
